import { useScienceTutorSection } from './scienceTutorContext';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useState } from 'react';

import type { AnswerCheck } from './gasLaws';
import { AnswerFeedback } from './GasLawsProblems';
import {
  WATER_PROPERTY_CASES,
  checkWaterChoice,
  checkWaterNumeric,
  waterPropertyCaseById,
  type WaterChoiceResult,
} from './waterChemistry';
import { WaterPropertyVisual } from './WaterChemistryVisuals';

export function WaterChemistryProperties() {
  const [caseId, setCaseId] = useState(WATER_PROPERTY_CASES[0].id);
  const [optionId, setOptionId] = useState<string | null>(null);
  const [answer, setAnswer] = useState('');
  useScienceTutorSection('WaterChemistryProperties', { caseId, optionId, answer });
  const [choiceResult, setChoiceResult] = useState<WaterChoiceResult | null>(null);
  const [numericResult, setNumericResult] = useState<AnswerCheck | null>(null);
  const [completed, setCompleted] = useState<string[]>([]);
  const item = waterPropertyCaseById(caseId) ?? WATER_PROPERTY_CASES[0];
  const revealed =
    choiceResult !== null || (numericResult !== null && numericResult.status !== 'invalid');

  const reset = () => {
    setOptionId(null);
    setAnswer('');
    setChoiceResult(null);
    setNumericResult(null);
  };
  const choose = (id: string) => {
    setCaseId(id);
    reset();
  };
  const markDone = (correct: boolean) => {
    if (correct) setCompleted((current) => (current.includes(item.id) ? current : [...current, item.id]));
  };
  const check = () => {
    if (item.kind === 'choice') {
      if (optionId === null) return;
      const outcome = checkWaterChoice(item, optionId);
      setChoiceResult(outcome);
      markDone(outcome?.correct ?? false);
      return;
    }
    const outcome = checkWaterNumeric(item, answer.trim() === '' ? Number.NaN : Number(answer));
    setNumericResult(outcome);
    markDone(outcome.status === 'correct');
  };

  return (
    <section
      aria-label="Water properties cases"
      className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className="text-sm font-medium leading-6 text-slate2">
          Predict or calculate first. The reference data chart appears after you check.
        </p>
        <span className="shrink-0 rounded-full bg-wash-mint px-3 py-1 text-xs font-extrabold text-ink-soft">
          {completed.length} of {WATER_PROPERTY_CASES.length} correct
        </span>
      </div>
      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Choose a water property case">
        {WATER_PROPERTY_CASES.map((candidate) => (
          <button
            key={candidate.id}
            type="button"
            aria-pressed={candidate.id === item.id}
            onClick={() => choose(candidate.id)}
            className={`rounded-full border px-3 py-1.5 text-xs font-extrabold ${
              candidate.id === item.id ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-white text-slate2'
            }`}
          >
            {completed.includes(candidate.id) ? '✓ ' : ''}
            {candidate.title}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          <h3 className="text-lg font-extrabold text-ink">{item.title}</h3>
          <p className="mt-2 font-medium leading-7 text-ink-soft">{item.prompt}</p>
          {item.kind === 'choice' ? (
            <div className="mt-4 grid gap-2" role="group" aria-label="Your prediction">
              {item.options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={optionId === option.id}
                  onClick={() => {
                    setOptionId(option.id);
                    setChoiceResult(null);
                  }}
                  className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold transition-colors motion-reduce:transition-none ${
                    optionId === option.id ? 'border-brand-coral bg-wash-coral text-ink' : 'border-hairline bg-white text-ink-soft'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : (
            <label className="mt-4 block text-sm font-extrabold text-ink">
              Your answer for {item.answerLabel} ({item.unit})
              <input
                type="number"
                inputMode="decimal"
                value={answer}
                onChange={(event) => {
                  setAnswer(event.target.value);
                  setNumericResult(null);
                }}
                className="mt-2 block w-full max-w-xs rounded-2xl border border-hairline bg-white px-4 py-3 text-base font-bold text-ink"
              />
            </label>
          )}
          <button
            type="button"
            onClick={check}
            disabled={item.kind === 'choice' ? optionId === null : answer.trim() === ''}
            className="mt-4 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            Check
          </button>
          <div className="mt-4">
            {choiceResult ? (
              <div
                role="status"
                className={`rounded-[1.5rem] border p-4 ${choiceResult.correct ? 'border-brand-mint/45 bg-wash-mint' : 'border-brand-coral/45 bg-wash-coral'}`}
              >
                <p className="flex items-center gap-2 font-extrabold text-ink">
                  {choiceResult.correct ? (
                    <CheckCircle2 className="h-5 w-5 text-brand-mint" aria-hidden="true" />
                  ) : (
                    <XCircle className="h-5 w-5 text-brand-coral" aria-hidden="true" />
                  )}
                  {choiceResult.correct ? 'Correct' : 'Not yet'}
                </p>
                <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">{choiceResult.feedback}</p>
                <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">{item.takeaway}</p>
              </div>
            ) : null}
            {numericResult && item.kind === 'numeric' ? (
              <AnswerFeedback check={numericResult} working={item.working} />
            ) : null}
          </div>
        </div>

        <div className="min-w-0 rounded-[1.5rem] border border-brand-sky/25 bg-white p-4">
          {revealed ? (
            <WaterPropertyVisual visual={item.visual} />
          ) : (
            <p className="rounded-2xl bg-surface px-4 py-3 text-sm font-medium leading-6 text-slate2">
              Reference data is hidden until you check your prediction.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
